/**
 * Ячейка URL (§5.2, тип «URL»).
 *
 * В ячейке показывается путь `/brands/aeg`; если хост не `high-level.ru` —
 * адрес целиком цветом `--link` (у всех 178 заполненных URL хост свой, §0).
 * Пустое значение кликабельно: «⌁ добавить URL» (П3). При вставке адрес
 * нормализуется, невалидное подчёркивается волной и объясняется строкой §12.7.
 */
import { useEffect, useRef, useState } from 'react'
import { IconExternalLink, Input } from '../../../../components/index.js'
import { tableTexts as t } from '../../texts.table.js'
import { isUrlLike, normalizeUrl, urlView } from '../values.js'
import type { CellProps } from './CellBox.js'

export function UrlCell(props: CellProps) {
  const { column, cell, editing, readOnly, seed, write, stepAfterCommit, commitAndRight, closeEditor } = props
  const published = typeof cell.value === 'string' ? cell.value : ''
  const view = urlView(cell.value)
  const [value, setValue] = useState(seed ?? published)
  const latest = useRef(value)

  useEffect(() => {
    if (!editing) return
    setValue(seed ?? published)
    latest.current = seed ?? published
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing])

  if (!editing || readOnly) {
    if (cell.empty) {
      return (
        <span className="cell__empty">
          <span className="cell__empty-dash" aria-hidden="true">{t.empty.dash}</span>
          <span className="cell__empty-label">
            <span className="cell__empty-glyph" aria-hidden="true">⌁</span>
            {t.empty.url}
          </span>
        </span>
      )
    }
    return (
      <span className="cell__value">
        {/* Пометка кабинета: без неё Розница и Опт выглядят одинаково. */}
        {view.site ? <span className="cell__url-site">{view.site}</span> : null}
        <span className={view.valid ? 'cell__link' : 'cell__link cell__url-invalid'}>{view.text}</span>
        {view.href ? (
          <a
            className="cell__url-open"
            href={view.href}
            target="_blank"
            rel="noreferrer noopener"
            aria-label={`${column.title}: ${view.text}`}
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <IconExternalLink size={16} />
          </a>
        ) : null}
      </span>
    )
  }

  const flush = () => {
    const next = latest.current.trim() === '' ? null : normalizeUrl(latest.current)
    if (next !== published) write(next)
  }

  const invalid = !isUrlLike(latest.current.trim())

  return (
    <div
      className="cell__editor"
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.preventDefault()
          event.stopPropagation()
          closeEditor()
          return
        }
        if (event.key === 'Enter') {
          event.preventDefault()
          event.stopPropagation()
          flush()
          stepAfterCommit(event.shiftKey ? -1 : 1)
          return
        }
        if (event.key === 'Tab') {
          event.preventDefault()
          event.stopPropagation()
          flush()
          commitAndRight()
        }
      }}
    >
      <Input
        autoFocus
        size="cell"
        value={value}
        invalid={invalid}
        aria-label={column.title}
        onChange={(event) => { setValue(event.target.value); latest.current = event.target.value }}
        onBlur={flush}
      />
    </div>
  )
}
